import React, { useEffect, useState, useCallback, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPoojas, fetchPoojaById, togglePoojaStatus,
  updatePooja, createPoojaMapping, clearSelectedPooja, createPooja
} from '../../Redux/Reducers/PoojaReducer';
import {
  Box, Switch, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Button, TablePagination, Backdrop,
  CircularProgress, Chip, TextField, Grid, FormControlLabel, Checkbox,
  Divider, Typography, InputAdornment, Tooltip, MenuItem, Select,
  FormControl, InputLabel, Accordion, AccordionSummary, AccordionDetails,
  Avatar, Pagination, List, ListItem, ListItemText, ListItemButton, ListItemIcon
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BsDownload, BsFilterLeft, BsFilterRight } from 'react-icons/bs';
import Autocomplete from "@mui/material/Autocomplete";
import debounce from "lodash/debounce";
import * as XLSX from 'xlsx';
import { validateFile } from '../../utils/comman-function';

const REGION_OPTIONS = ['India', 'ROW'];
// const PEOPLE_TYPE_OPTIONS = ['Individual', 'Group'];
const DAYS_OPTIONS = ["All", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const SEARCH_BY_OPTIONS = [
  { value: "poojaName", label: "Pooja Name" },
  { value: "basePrice", label: "Base Price" },
  { value: "duration", label: "Duration" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];


// ─── Helper: empty edit form ─────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', description: '', image: '', benefits: '',
  keywords: [], /* peopleType: 'Individual', */ basePrice: '',
  duration: '', categoryId: [], poojaTagId: '', regions: [], isTrending: false,
  preferredDays: [],
  whatHappensAfterOrder: '',
  procedure: '',
  startDateTime: '',
  endDateTime: '',
  bookingOpenTill: '',
  includedServices: {
    sankalp: false,
    templePhotos: false,
    videoCall: false,
    poojaVideo: false,
    prasadDispatch: false,
    personalizedMantra: false
  },
  poojaFaqId: []
};

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const convertLocalStringToUTC = (localStr) => {
  if (!localStr) return '';
  const [datePart, timePart] = localStr.split('T');
  if (!datePart || !timePart) return '';
  const [year, month, day] = datePart.split('-');
  const [hour, min] = timePart.split(':');
  return new Date(year, month - 1, day, hour, min).toISOString();
};


function PoojaList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [poojaEnabled, setPoojaEnabled] = useState(false);
  const [globalToggleLoading, setGlobalToggleLoading] = useState(false);

  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  const initialSearchByValue = searchParams.get('searchBy') || SEARCH_BY_OPTIONS[0].value;
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || STATUS_OPTIONS[0].value;
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialBookingOpenTill = searchParams.get('bookingOpenTill') || '';
  const initialCreatedAt = searchParams.get('createdAt') || '';

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });

  const [searchBy, setSearchBy] = useState(SEARCH_BY_OPTIONS.find(o => o.value === initialSearchByValue) || SEARCH_BY_OPTIONS[0]);
  const [searchText, setSearchText] = useState(initialSearch);
  const [status, setStatus] = useState(initialStatus);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [bookingOpenTill, setBookingOpenTill] = useState(initialBookingOpenTill);
  const [createdAt, setCreatedAt] = useState(initialCreatedAt);

  const [appliedFilters, setAppliedFilters] = useState({
    searchByValue: initialSearchByValue,
    searchText: initialSearch,
    status: initialStatus,
    startDate: initialStartDate,
    endDate: initialEndDate,
    bookingOpenTill: initialBookingOpenTill,
    createdAt: initialCreatedAt,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isApplied, setIsApplied] = useState(
    Boolean(initialSearch.trim() || initialStatus !== 'all' || initialStartDate || initialEndDate || initialBookingOpenTill || initialCreatedAt)
  );
  const [showFilters, setShowFilters] = useState(false);

  const getFetchParams = (overrides = {}) => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) limit = 10;

    const params = {
      page: (overrides.page !== undefined ? overrides.page : page) + 1,
      limit,
    };

    if (isApplied) {
      const trimmed = (appliedFilters.searchText || '').trim();
      if (trimmed) {
        params.searchBy = appliedFilters.searchByValue;
        params.search = trimmed;
      }
      if (appliedFilters.status !== 'all') params.status = appliedFilters.status;
      if (appliedFilters.startDate) params.startDate = convertLocalStringToUTC(appliedFilters.startDate);
      if (appliedFilters.endDate) params.endDate = convertLocalStringToUTC(appliedFilters.endDate);
      if (appliedFilters.bookingOpenTill) params.bookingOpenTill = convertLocalStringToUTC(appliedFilters.bookingOpenTill);
      if (appliedFilters.createdAt) params.createdAt = appliedFilters.createdAt;
    }

    return params;
  };

  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const fetchGlobalPoojaToggle = async () => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/poojaToggle`, {
        headers: { Authorization: token }
      });
      if (response.ok) {
        const result = await response.json();
        const value = result?.data?.value?.isEnabled ?? result?.value?.isEnabled ?? result?.data?.isEnabled ?? result?.isEnabled ?? false;
        setPoojaEnabled(value);
      }
    } catch (error) {
      console.error("Error fetching global pooja toggle:", error);
    }
  };

  const handleToggleGlobalPooja = async (event) => {
    const newValue = event.target.checked;
    setGlobalToggleLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/poojaToggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: {
            isEnabled: newValue
          }
        })
      });
      const result = await response.json();
      if (response.ok) {
        setPoojaEnabled(newValue);
        toast.success(`Pooja globally ${newValue ? 'enabled' : 'disabled'} successfully.`);
      } else {
        toast.error(result.message || 'Failed to update global pooja toggle status');
      }
    } catch (error) {
      console.error("Error toggling global pooja:", error);
      toast.error('Failed to update global pooja toggle status');
    } finally {
      setGlobalToggleLoading(false);
    }
  };

  useEffect(() => {
    fetchGlobalPoojaToggle();
  }, []);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/category?page=1&limit=100`,
        { headers: { Authorization: token } }
      );
      const result = await response.json();
      if (response.ok) {
        const catData = result?.data?.data || result?.data || result?.results || [];
        setCategories(Array.isArray(catData) ? catData : []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const [poojaTags, setPoojaTags] = useState([]);
  const [poojaTagsLoading, setPoojaTagsLoading] = useState(false);

  const fetchPoojaTags = async () => {
    try {
      setPoojaTagsLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/tag?page=1&limit=100`,
        { headers: { Authorization: token } }
      );
      const result = await response.json();
      if (response.ok) {
        const tagData = result?.data?.data || result?.data || result?.results || [];
        setPoojaTags(Array.isArray(tagData) ? tagData : []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPoojaTagsLoading(false);
    }
  };

  const [poojaFaqs, setPoojaFaqs] = useState([]);
  const [poojaFaqsLoading, setPoojaFaqsLoading] = useState(false);

  const [faqSelectOpen, setFaqSelectOpen] = useState(false);
  const [faqSelectPage, setFaqSelectPage] = useState(1);
  const [faqPaginationDetail, setFaqPaginationDetail] = useState({ totalDocs: 0, totalPages: 1 });
  const [tempSelectedFaqs, setTempSelectedFaqs] = useState([]);

  const fetchPoojaFaqs = async (page = 1) => {
    try {
      setPoojaFaqsLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/faq?page=${page}&limit=10`,
        { headers: { Authorization: token } }
      );
      const result = await response.json();
      if (response.ok) {
        const poojaFaqData = result?.data || [];
        setPoojaFaqs(Array.isArray(poojaFaqData) ? poojaFaqData : []);
        if (result.paginationDetail) {
          setFaqPaginationDetail(result.paginationDetail);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPoojaFaqsLoading(false);
    }
  };

  const handleToggleTempFaq = (id) => {
    setTempSelectedFaqs((prev) => 
      prev.includes(id) ? prev.filter((faqId) => faqId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    fetchCategories();
    fetchPoojaTags();
    fetchPoojaFaqs();
  }, []);

  const [astrologers, setAstrologers] = useState([]);
  const [astroLoading, setAstroLoading] = useState(false);
  const [astroSearch, setAstroSearch] = useState("");
  const [astroPage, setAstroPage] = useState(1);
  const [astroPagination, setAstroPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
  });

  const [mapPoojaId, setMapPoojaId] = useState(null);
  const mapPoojaIdRef = useRef(null);
  useEffect(() => {
    mapPoojaIdRef.current = mapPoojaId;
  }, [mapPoojaId]);

  const fetchAstrologers = async (
    page = 1,
    search = "",
    currentPoojaId = null
  ) => {
    try {
      setAstroLoading(true);

      const token = localStorage.getItem(
        "astroVani-admin-token"
      );

      const targetPoojaId = currentPoojaId || mapPoojaIdRef.current || mapPoojaId;
      const poojaParam = targetPoojaId ? `&poojaId=${encodeURIComponent(targetPoojaId)}` : "";

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/getAllAstroVerified?page=${page}&limit=20&isAvailableforPooja=true${search
          ? `&fullName=${encodeURIComponent(search)}`
          : ""
        }${poojaParam}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        setAstrologers(result?.data || []);

        setAstroPagination({
          totalDocs:
            result?.paginationDetail?.totalDocs || 0,
          totalPages:
            result?.paginationDetail?.totalPages || 1,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setAstroLoading(false);
    }
  };

  // ── Dialog state ────────────────────────────────────────────────────────────
  // View popup
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewPooja, setViewPooja] = useState(null);

  const [mappedAstroLoading, setMappedAstroLoading] = useState(false);
  const [mappedAstrologers, setMappedAstrologers] = useState([]);
  const [mappedAstroPage, setMappedAstroPage] = useState(1);
  const [mappedAstroTotal, setMappedAstroTotal] = useState(0);

  const [unmapConfirmOpen, setUnmapConfirmOpen] = useState(false);
  const [unmapMappingId, setUnmapMappingId] = useState(null);
  const [unmapLoading, setUnmapLoading] = useState(false);

  const fetchMappedAstrologers = async (poojaId, page = 1) => {
    setMappedAstroLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/astrologers/${poojaId}?page=${page}&limit=10`, {
        headers: { Authorization: token }
      });
      const result = await response.json();
      if (response.ok) {
        setMappedAstrologers(result?.data || []);
        setMappedAstroTotal(result?.paginationDetail?.totalDocs || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setMappedAstroLoading(false);
    }
  };

  const handleUnmapAstrologer = async () => {
    if (!unmapMappingId) return;
    setUnmapLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/unmap/${unmapMappingId}`, {
        method: 'DELETE',
        headers: { Authorization: token }
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Astrologer unmapped successfully");
        setUnmapConfirmOpen(false);
        setUnmapMappingId(null);
        fetchMappedAstrologers(viewPooja._id || viewPooja.id, mappedAstroPage);
      } else {
        toast.error(result?.message || "Failed to unmap astrologer");
      }
    } catch (err) {
      toast.error("Failed to unmap astrologer");
    } finally {
      setUnmapLoading(false);
    }
  };

  // Edit popup
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  // Mapping popup
  const [mapOpen, setMapOpen] = useState(false);
  const [mapPoojaName, setMapPoojaName] = useState('');
  const [mapPoojaDetails, setMapPoojaDetails] = useState(null);
  const [mappingRows, setMappingRows] = useState([
    {
      astrologerId: "",
      finalUserPrice: "",
      finalOfferUserPrice: "",
      companyShare: "",
      astrologerShare: "",
      poojaDescription: "",
    },
  ]);
  const [mapLoading, setMapLoading] = useState(false);

  const didInitUrlSyncRef = useRef(false);

  const { poojaData = [], pagination, loading: poojaLoading } =
    useSelector((state) => state?.PoojaReducer || {});

  // ── Fetch list ───────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchPoojas(getFetchParams()));
  }, [dispatch, paginationModel.page, paginationModel.pageSize, isApplied, appliedFilters]);

  // ── URL sync ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!didInitUrlSyncRef.current) { didInitUrlSyncRef.current = true; return; }
    const params = new URLSearchParams();
    params.set('page', String(paginationModel.page));
    params.set('pageSize', String(paginationModel.pageSize));

    if (isApplied) {
      const trimmed = (appliedFilters.searchText || '').trim();
      if (trimmed) {
        params.set('searchBy', appliedFilters.searchByValue);
        params.set('search', trimmed);
      }
      if (appliedFilters.status !== 'all') params.set('status', appliedFilters.status);
      if (appliedFilters.startDate) params.set('startDate', appliedFilters.startDate);
      if (appliedFilters.endDate) params.set('endDate', appliedFilters.endDate);
      if (appliedFilters.bookingOpenTill) params.set('bookingOpenTill', appliedFilters.bookingOpenTill);
      if (appliedFilters.createdAt) params.set('createdAt', appliedFilters.createdAt);
    }

    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, isApplied, appliedFilters, setSearchParams]);

  // ── Toggle status ─────────────────────────────────────────────────────────────
  const handleToggleStatus = (id, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [`toggle-${id}`]: true }));
    dispatch(togglePoojaStatus({ id, isActive: !currentStatus }))
      .unwrap()
      .then(() => {
        toast.success(`Pooja ${!currentStatus ? 'activated' : 'deactivated'} successfully.`);
        const { page } = paginationModel;
        dispatch(fetchPoojas(getFetchParams({ page })));
      })
      .catch((err) => toast.error(err?.message || err || 'Failed to update status'))
      .finally(() => setActionLoading((prev) => ({ ...prev, [`toggle-${id}`]: false })));
  };

  const handleToggleFeature = async (id, feature, currentValue) => {
    setActionLoading((prev) => ({ ...prev, [`${feature}-${id}`]: true }));
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      let url = '';
      let payload = {};

      if (feature === 'trending') {
        url = `${process.env.REACT_APP_BASEURL}/admin/pooja/trending/${id}`;
        payload = { isTrending: !currentValue };
      } else if (feature === 'recommended') {
        url = `${process.env.REACT_APP_BASEURL}/admin/pooja/recommended/${id}`;
        payload = { isRecommended: !currentValue };
      } else if (feature === 'negativeEnergyRemoval') {
        url = `${process.env.REACT_APP_BASEURL}/admin/pooja/negativeEnergyRemoval/${id}`;
        payload = { isNegativeEnergyRemoval: !currentValue };
      }

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok) {
        toast.success(`${feature} status updated successfully.`);
        const { page } = paginationModel;
        dispatch(fetchPoojas(getFetchParams({ page })));
      } else {
        toast.error(result.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading((prev) => ({ ...prev, [`${feature}-${id}`]: false }));
    }
  };

  // ── VIEW popup ────────────────────────────────────────────────────────────────
  const handleOpenView = async (id) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewPooja(null);
    setMappedAstroPage(1);
    try {
      const result = await dispatch(fetchPoojaById(id)).unwrap();
      setViewPooja(result);
      fetchMappedAstrologers(id, 1);
    } catch {
      toast.error('Failed to load pooja details');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
    setEditOpen(true);
    setEditLoading(false);
  };

  // ── EDIT popup ────────────────────────────────────────────────────────────────
  const handleOpenEdit = async (id) => {
    setEditId(id);
    setEditOpen(true);
    setEditLoading(true);
    try {
      const result = await dispatch(fetchPoojaById(id)).unwrap();
      setEditForm({
        name: result.name || '',
        description: result.description || '',
        image: result.image || '',
        benefits: result.benefits || '',
        keywords: Array.isArray(result.keywords) ? result.keywords : (result.keywords ? [result.keywords] : []),
        // peopleType: result.peopleType || 'Individual',
        basePrice: result.basePrice != null ? result.basePrice : '',
        duration: result.duration != null ? result.duration : '',
        categoryId: Array.isArray(result.categoryId) ? result.categoryId.map(c => typeof c === 'object' && c !== null ? (c._id || c.id) : c) : (typeof result.categoryId === 'object' && result.categoryId !== null ? [(result.categoryId._id || result.categoryId.id)] : (result.categoryId ? [result.categoryId] : [])),
        poojaTagId: typeof result.poojaTagId === 'object' && result.poojaTagId !== null ? (result.poojaTagId._id || result.poojaTagId.id) : (result.poojaTagId || ''),
        regions: Array.isArray(result?.regions) ? result?.regions : [],
        isTrending: result.isTrending || false,
        preferredDays: Array.isArray(result?.preferredDays) ? result?.preferredDays : [],
        whatHappensAfterOrder: result.whatHappensAfterOrder || '',
        procedure: result.procedure || '',
        startDateTime: formatDateForInput(result.startDateTime),
        endDateTime: formatDateForInput(result.endDateTime),
        bookingOpenTill: formatDateForInput(result.bookingOpenTill),
        includedServices: {
          sankalp: result.includedServices?.sankalp || false,
          templePhotos: result.includedServices?.templePhotos || false,
          videoCall: result.includedServices?.videoCall || false,
          poojaVideo: result.includedServices?.poojaVideo || false,
          prasadDispatch: result.includedServices?.prasadDispatch || false,
          personalizedMantra: result.includedServices?.personalizedMantra || false,
        },
        poojaFaqId: Array.isArray(result.poojaFaqId) ? result.poojaFaqId.map(f => typeof f === 'object' ? (f._id || f.id) : f) : [],
      });
    } catch {
      toast.error('Failed to load pooja details');
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditChange = (field, value) =>
    setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validateFile(file, "image")) {
        return;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        const token = localStorage.getItem("astroVani-admin-token");

        const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/imageUpload`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: token,
          },
        });

        if (!response.ok) throw new Error("Failed to upload image");

        const responseData = await response.json();
        const imageUrl = responseData?.data?.img[0];

        if (imageUrl) handleEditChange('image', imageUrl);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image.");
      }
    }
  };

  const handleIncludedServiceChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      includedServices: {
        ...prev.includedServices,
        [field]: value
      }
    }));
  };

  const handleEditRegionToggle = (region) => {
    setEditForm((prev) => {
      const exists = prev.regions.includes(region);
      return {
        ...prev,
        regions: exists ? prev.regions.filter((r) => r !== region) : [...prev.regions, region],
      };
    });
  };

  const handleEditSubmit = async () => {
    if (!editForm.name.trim()) { toast.error('Name is required'); return; }
    if (editForm.name.trim().length > 120) { toast.error('Pooja Name cannot exceed 120 characters'); return; }
    if (!editForm.image) { toast.error('Image is required'); return; }
    if (!editForm.basePrice) { toast.error('Base price is required'); return; }
    if (!editForm.duration) { toast.error('Duration is required'); return; }
    if (editForm.description && editForm.description.length > 1000) { toast.error('Description cannot exceed 1000 characters'); return; }
    if (editForm.benefits && editForm.benefits.length > 1000) { toast.error('Benefits cannot exceed 1000 characters'); return; }
    if (editForm.whatHappensAfterOrder && editForm.whatHappensAfterOrder.length > 300) { toast.error('What Happens After Order cannot exceed 300 characters'); return; }
    if (editForm.procedure && editForm.procedure.length > 300) { toast.error('Procedure cannot exceed 300 characters'); return; }
    if (!editForm.preferredDays || editForm.preferredDays.length === 0) { toast.error('Preferred Days are required'); return; }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!editId && editForm.startDateTime && new Date(editForm.startDateTime) < today) {
      toast.error('Start Date Time cannot be before today');
      return;
    }
    if (!editId && editForm.endDateTime && new Date(editForm.endDateTime) < today) {
      toast.error('End Date Time cannot be before today');
      return;
    }
    if (editForm.startDateTime && editForm.endDateTime) {
      if (new Date(editForm.startDateTime) >= new Date(editForm.endDateTime)) {
        toast.error('Start Date Time must be before End Date Time');
        return;
      }
    }
    if (editForm.startDateTime && editForm.endDateTime && editForm.bookingOpenTill) {
      const start = new Date(editForm.startDateTime);
      const end = new Date(editForm.endDateTime);
      const booking = new Date(editForm.bookingOpenTill);
      if (booking < start || booking > end) {
        toast.error('Booking Open Till must be between Start and End Date Time');
        return;
      }
    }

    setActionLoading((prev) => ({ ...prev, edit: true }));
    const payload = {
      ...editForm,
      startDateTime: convertLocalStringToUTC(editForm.startDateTime),
      endDateTime: convertLocalStringToUTC(editForm.endDateTime),
      bookingOpenTill: convertLocalStringToUTC(editForm.bookingOpenTill),
      keywords: Array.isArray(editForm.keywords) ? editForm.keywords?.map(k => k.trim()).filter(Boolean) : [],
      poojaFaqId: Array.isArray(editForm.poojaFaqId) ? editForm.poojaFaqId : [],
      basePrice: Number(editForm.basePrice),
      duration: Number(editForm.duration),
    };
    if (!payload.categoryId || payload.categoryId.length === 0) delete payload.categoryId;
    if (!payload.poojaTagId) delete payload.poojaTagId;

    try {
      if (editId) {
        await dispatch(updatePooja({ id: editId, payload })).unwrap();
        toast.success('Pooja updated successfully!');
      } else {
        await dispatch(createPooja(payload)).unwrap();
        toast.success('Pooja created successfully!');
      }
      setEditOpen(false);
      setEditForm(EMPTY_FORM);
      const { page } = paginationModel;
      dispatch(fetchPoojas(getFetchParams({ page })));
    } catch (err) {
      toast.error(err || `Failed to ${editId ? 'update' : 'create'} pooja`);
    } finally {
      setActionLoading((prev) => ({ ...prev, edit: false }));
    }
  };

  // ── MAPPING popup ─────────────────────────────────────────────────────────────
  const handleOpenMapping = (id, row) => {
    setMapPoojaId(id);
    mapPoojaIdRef.current = id;
    setMapPoojaName(row.name);
    setMapPoojaDetails({
      category: row.categoryName || '-',
      duration: row.duration,
      basePrice: row.basePrice,
    });

    setMappingRows([
      {
        astrologerId: "",
        selectedAstrologer: null,
        finalUserPrice: "",
        finalOfferUserPrice: "",
        companyShare: "",
        astrologerShare: "",
        poojaDescription: "",
      },
    ]);

    setAstroPage(1);
    setAstroSearch("");
    fetchAstrologers(1, "", id);
    setMapOpen(true);
  };

  const addMappingRow = () => {
    setMappingRows((prev) => [
      ...prev,
      {
        astrologerId: "",
        selectedAstrologer: null,
        finalUserPrice: "",
        finalOfferUserPrice: "",
        companyShare: "",
        astrologerShare: "",
        poojaDescription: "",
      },
    ]);
  };

  const removeMappingRow = (index) => {
    setMappingRows((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const updateMappingRow = (index, field, value) => {
    setMappingRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const handleMappingSubmit = async () => {
    const selectedAstroIds = mappingRows.map((r) => r.astrologerId).filter(Boolean);
    if (new Set(selectedAstroIds).size !== selectedAstroIds.length) {
      toast.error("Duplicate astrologers are not allowed across mappings");
      return;
    }

    for (const row of mappingRows) {
      // Required fields
      if (
        !row.astrologerId ||
        !row.finalUserPrice ||
        !row.finalOfferUserPrice ||
        !row.companyShare ||
        !row.astrologerShare
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const finalUserPrice = Number(row.finalUserPrice);
      const offerPrice = Number(row.finalOfferUserPrice);
      const companyShare = Number(row.companyShare);
      const astrologerShare = Number(row.astrologerShare);

      // Final User Price >= Offer Price
      if (finalUserPrice < offerPrice) {
        toast.error(
          "Final User Price must be greater than or equal to Offer Price"
        );
        return;
      }

      // Share validation
      if (companyShare + astrologerShare !== 100) {
        toast.error(
          "Company Share % + Astrologer Share % must equal 100%"
        );
        return;
      }
    }

    setMapLoading(true);

    try {
      const payload = mappingRows.map((item) => ({
        poojaId: mapPoojaId,
        astrologerId: item.astrologerId,
        finalUserPrice: Number(item.finalUserPrice),
        finalOfferUserPrice: Number(item.finalOfferUserPrice),
        companyShare: Number(item.companyShare),
        astrologerShare: Number(item.astrologerShare),
        poojaDescription: item.poojaDescription || "",
      }));

      await dispatch(createPoojaMapping(payload)).unwrap();

      toast.success("Pooja mapping created successfully!");
      setMapOpen(false);
    } catch (err) {
      toast.error(err || "Failed to create mapping");
    } finally {
      setMapLoading(false);
    }
  };

  const debouncedAstroSearch = useCallback(
    debounce((value) => {
      setAstroPage(1);
      setAstroSearch(value);
      fetchAstrologers(1, value);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedAstroSearch.cancel();
    };
  }, [debouncedAstroSearch]);

  // ── Export ────────────────────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    const token = localStorage.getItem('astroVani-admin-token');
    setExportLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja`, {
        headers: { Authorization: token },
      });
      if (!res.ok) throw new Error('Export failed');
      const result = await res.json();
      const poojas = Array.isArray(result) ? result : result?.data || [];
      if (!poojas.length) { toast.error('No data to export'); return; }

      const headers = [
        'Name',
        'Description',
        'Base Price',
        'Duration (min)',
        'Regions',
        'Is Trending',
        'Is Recommended',
        'Is Negative Energy Removal',
        'Sankalp',
        'Temple Photos',
        'Video Call',
        'Pooja Creative',
        'Prasad Dispatch',
        'Personalized Mantra',
        'Is Active',
        'Start Time',
        'End Time',
        'Booking Open Till',
        'Created At'
      ];
      const rows = poojas.map((p) => [
        p.name || '',
        p.description || '',
        p.basePrice || '',
        p.duration || '',
        (p.regions || []).join(' | '),
        p.isTrending ? 'Yes' : 'No',
        p.isRecommended ? 'Yes' : 'No',
        p.isNegativeEnergyRemoval ? 'Yes' : 'No',
        p.includedServices?.sankalp ? 'Yes' : 'No',
        p.includedServices?.templePhotos ? 'Yes' : 'No',
        p.includedServices?.videoCall ? 'Yes' : 'No',
        p.includedServices?.poojaVideo ? 'Yes' : 'No',
        p.includedServices?.prasadDispatch ? 'Yes' : 'No',
        p.includedServices?.personalizedMantra ? 'Yes' : 'No',
        p.isActive ? 'Active' : 'Inactive',
        p.startDateTime ? new Date(p.startDateTime).toLocaleString() : '',
        p.endDateTime ? new Date(p.endDateTime).toLocaleString() : '',
        p.bookingOpenTill ? new Date(p.bookingOpenTill).toLocaleString() : '',
        p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Poojas');
      ws['!cols'] = headers.map((h) => ({ wch: h.length + 10 }));
      XLSX.writeFile(wb, 'poojas_export.xlsx');
      toast.success('Exported successfully!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  }, []);

  // ── Pagination ────────────────────────────────────────────────────────────────
  const handleChangePage = (e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) });

  const handleApplyFilters = () => {
    if (startDate && endDate && bookingOpenTill) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const booking = new Date(bookingOpenTill);
      if (!(start < booking && booking < end)) {
        toast.error("Start Time must be before Booking Open Till, and Booking Open Till must be before End Time");
        return;
      }
    } else if (startDate && endDate) {
      if (new Date(startDate) >= new Date(endDate)) {
        toast.error("Start Time must be before End Time");
        return;
      }
    }

    setAppliedFilters({
      searchByValue: searchBy.value,
      searchText: searchText,
      status: status,
      startDate: startDate,
      endDate: endDate,
      bookingOpenTill: bookingOpenTill,
      createdAt: createdAt,
    });
    setIsApplied(true);
    setIsDirty(false);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    setSearchBy(SEARCH_BY_OPTIONS[0]);
    setSearchText("");
    setStatus(STATUS_OPTIONS[0].value);
    setStartDate("");
    setEndDate("");
    setBookingOpenTill("");
    setCreatedAt("");

    setAppliedFilters({
      searchByValue: SEARCH_BY_OPTIONS[0].value,
      searchText: "",
      status: STATUS_OPTIONS[0].value,
      startDate: "",
      endDate: "",
      bookingOpenTill: "",
      createdAt: "",
    });
    setIsApplied(false);
    setIsDirty(false);
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  // ── Columns ───────────────────────────────────────────────────────────────────
  const columns = [
    { field: 'sno', headerName: 'S.No', width: 65, filterable: false, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false, filterable: false, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleOpenView(row.id)}
              sx={{ color: '#7b1fa2', '&:hover': { backgroundColor: 'rgba(123,31,162,0.08)' } }}>
              <i className="icofont-eye" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Pooja">
            <IconButton size="small" onClick={() => handleOpenEdit(row.id)}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}>
              <i className="icofont-edit" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Astrologer Mapping">
            <IconButton size="small" onClick={() => handleOpenMapping(row.id, row)}
              sx={{ color: '#388e3c', '&:hover': { backgroundColor: 'rgba(56,142,60,0.08)' } }}>
              <i className="icofont-link" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'name', headerName: 'Pooja Name', width: 200,
      renderCell: (params) => <span style={{ fontWeight: 500 }}>{params.value || '-'}</span>,
    },
    {
      field: 'basePrice', headerName: 'Base Price', width: 120, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Chip label={`₹${row.basePrice ?? '-'}`} size="small" color="success" variant="outlined"
          sx={{ fontSize: '12px', fontWeight: 600 }} />
      ),
    },
    {
      field: 'duration', headerName: 'Duration', width: 110, headerAlign: 'center', align: 'center',
      valueFormatter: (value) => value != null ? `${value} min` : '-',
    },
    {
      field: 'videoCall', headerName: 'Video Call', width: 110, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => {
        const isVideoCall = row.includedServices?.videoCall;
        return (
          <Chip label={isVideoCall ? 'Yes' : 'No'} size="small" color={isVideoCall ? 'success' : 'default'} sx={{ fontWeight: 600 }} />
        );
      },
    },
    {
      field: 'isTrending', headerName: 'Trending', width: 95, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Switch checked={Boolean(row.isTrending)} onChange={() => handleToggleFeature(row.id, 'trending', row.isTrending)}
          color="warning" disabled={!!actionLoading[`trending-${row.id}`]} size="small" />
      ),
    },
    {
      field: 'isRecommended', headerName: 'Recommended', width: 120, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Switch checked={Boolean(row.isRecommended)} onChange={() => handleToggleFeature(row.id, 'recommended', row.isRecommended)}
          color="secondary" disabled={!!actionLoading[`recommended-${row.id}`]} size="small" />
      ),
    },
    {
      field: 'isNegativeEnergyRemoval', headerName: 'Neg Energy Rem', width: 140, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Switch checked={Boolean(row.isNegativeEnergyRemoval)} onChange={() => handleToggleFeature(row.id, 'negativeEnergyRemoval', row.isNegativeEnergyRemoval)}
          color="info" disabled={!!actionLoading[`negativeEnergyRemoval-${row.id}`]} size="small" />
      ),
    },
    {
      field: 'isActive', headerName: 'Active', width: 90, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Switch checked={Boolean(row.isActive)} onChange={() => handleToggleStatus(row.id, row.isActive)}
          color="primary" disabled={!!actionLoading[`toggle-${row.id}`]} size="small" />
      ),
    },
    {
      field: 'startDateTime', headerName: 'Start Time', width: 170, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'endDateTime', headerName: 'End Time', width: 170, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'bookingOpenTill', headerName: 'Booking Open Till', width: 170, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'createdAt', headerName: 'Created At', width: 170, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
  ];

  const rows = (poojaData || []).map((item, index) => {
    if (!item || !item._id) return null;
    return {
      sno: paginationModel.page * paginationModel.pageSize + index + 1,
      id: item._id,
      name: item.name || '-',
      basePrice: item.basePrice,
      duration: item.duration,
      categoryName: Array.isArray(item.categoryId) 
        ? item.categoryId.map(c => typeof c === 'object' && c !== null ? (c.categoryName || c.name || '-') : c).join(', ')
        : (typeof item.categoryId === 'object' && item.categoryId !== null ? (item.categoryId.categoryName || item.categoryId.name || '-') : (item.categoryId || '-')),
      regions: item.regions || [],
      isTrending: item.isTrending || false,
      isRecommended: item.isRecommended || false,
      isNegativeEnergyRemoval: item.isNegativeEnergyRemoval || false,
      includedServices: item.includedServices || {},
      isActive: item.isActive !== undefined ? item.isActive : true,
      startDateTime: item.startDateTime ? new Date(item.startDateTime) : null,
      endDateTime: item.endDateTime ? new Date(item.endDateTime) : null,
      bookingOpenTill: item.bookingOpenTill ? new Date(item.bookingOpenTill) : null,
      createdAt: item.createdAt ? new Date(item.createdAt) : null,
    };
  }).filter(Boolean);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1
          pagetitle="Pooja List"
          modalbutton={() => (
            <Box display="flex" alignItems="center" gap={2}>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                  startIcon={showFilters ? <BsFilterRight /> : <BsFilterLeft />}
                  borderRight={'1px solid #e0e0e0'}
                  sx={{
                    fontSize: '13px',
                    px: 2,
                    py: 0.75,
                    borderRadius: '8px',
                    textTransform: 'none',
                    height: '35px',
                  }}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: '#fff', px: 2, py: 0.5, borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight={600} color={poojaEnabled ? 'success.main' : 'text.secondary'}>
                  Pooja Status: {poojaEnabled ? 'Enabled' : 'Disabled'}
                </Typography>
                {globalToggleLoading ? (
                  <CircularProgress size={20} sx={{ mx: 1 }} />
                ) : (
                  <Switch
                    checked={poojaEnabled}
                    onChange={handleToggleGlobalPooja}
                    color="success"
                  />
                )}
              </Box>
            </Box>
          )}
        />
        <div className="card mb-3" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', padding: '20px' }}>
         
            <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" sx={{ gap: 2, mb: 3 }}>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Search By</label>
                <select
                  value={searchBy.value}
                  onChange={(e) => {
                    setSearchBy(SEARCH_BY_OPTIONS.find(o => o.value === e.target.value));
                    setIsDirty(true);
                  }}
                  className="form-select form-select-sm"
                >
                  {SEARCH_BY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Search Text</label>
                <input
                  type={searchBy.value === 'basePrice' || searchBy.value === 'duration' ? 'number' : 'text'}
                  placeholder={`Enter ${searchBy.label.toLowerCase()}...`}
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setIsDirty(true); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDirty) handleApplyFilters();
                  }}
                  className="form-control form-control-sm"
                />
              </Box>
            {showFilters && ( 
              <>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => { setStatus(e.target.value); setIsDirty(true); }}
                  className="form-select form-select-sm"
                >
                  {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setIsDirty(true); }}
                  className="form-control form-control-sm"
                />
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Booking Open Till</label>
                <input
                  type="datetime-local"
                  value={bookingOpenTill}
                  onChange={(e) => { setBookingOpenTill(e.target.value); setIsDirty(true); }}
                  className="form-control form-control-sm"
                />
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setIsDirty(true); }}
                  className="form-control form-control-sm"
                />
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Created At</label>
                <input
                  type="date"
                  value={createdAt}
                  onChange={(e) => { setCreatedAt(e.target.value); setIsDirty(true); }}
                  className="form-control form-control-sm"
                />
              </Box>
              </>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  disabled={!isDirty}
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{ textTransform: 'none', height: '31px' }}
                >
                  Apply
                </Button>
                <Button variant="outlined" disabled={!isApplied && !isDirty} onClick={handleResetFilters} size="small" sx={{ textTransform: 'none', height: '31px' }}>Reset</Button>
              </Box>
            </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button variant="contained"
                startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
                size="small" disabled={exportLoading || poojaLoading} onClick={handleExport}
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}>
                {exportLoading ? 'Exporting...' : 'Export'}
              </Button>
              <Button variant="contained" onClick={handleOpenCreate} size="small"
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}>
                + Add Pooja
              </Button>
            </Box>
          </Box>
        </div>
        <div className="row g-0">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid loading={poojaLoading} columns={columns} rows={rows}
                    disableRowSelectionOnClick paginationMode="server" hideFooterPagination
                    autoHeight getRowId={(row) => row.id} sx={{ border: 'none' }} />
                </Box>
                <TablePagination component="div" count={pagination?.totalDocs || 0}
                  page={paginationModel.page} onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize} onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Rows per page:" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className="icofont-lamp" style={{ color: '#E7B242', fontSize: '22px' }}></i>
            Pooja Details
          </Box>
          {viewPooja?._id && (() => {
            const isPreprod = process.env.REACT_APP_BASEURL?.includes("preprod");
            const domain = isPreprod ? "preprod.astrovani-balaji.store" : "balajiastroguide.com";
            const shareLink = `https://${domain}/pooja-details?poojaId=${viewPooja._id}`;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#f9f9f9', px: 2, py: 0.75, borderRadius: '8px', border: '1px solid #eaeaea' }}>
                <Typography variant="body2" sx={{ color: '#555', fontFamily: 'monospace', fontSize: '13px', userSelect: 'all', fontWeight: 500 }}>
                  {shareLink}
                </Typography>
                <Tooltip title="Copy Pooja Link">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(shareLink)
                        .then(() => toast.success('Pooja link copied to clipboard!'))
                        .catch(() => toast.error('Failed to copy link!'));
                    }}
                    size="small"
                    sx={{
                      color: '#E7B242',
                      backgroundColor: 'rgba(231,178,66,0.1)',
                      '&:hover': { backgroundColor: 'rgba(231,178,66,0.2)' }
                    }}
                  >
                    <i className="icofont-ui-copy" style={{ fontSize: '18px' }}></i>
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })()}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, px: 3, pb: 3, backgroundColor: '#f8f9fa' }}>
          {viewLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="secondary" />
            </Box>
          ) : viewPooja ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  backgroundColor: '#fff', borderRadius: '12px', p: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', height: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}>
                  {viewPooja.image ? (
                    <img src={viewPooja.image} alt={viewPooja.name} onError={(e) => { e.target.style.display = 'none'; }}
                      style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '8px', marginBottom: '16px' }} />
                  ) : (
                    <Box sx={{ width: '100%', height: '180px', backgroundColor: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <i className="icofont-image" style={{ fontSize: '40px', color: '#bdbdbd' }}></i>
                    </Box>
                  )}
                  <Typography variant="subtitle1" fontSize="15px" fontWeight={600} color="text.secondary" mb={1} borderBottom={'1px solid transparent'}>{viewPooja.name || '-'}</Typography>
                  <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap" mb={2}>
                    <Chip label={viewPooja.isActive ? 'Active' : 'Inactive'}
                      color={viewPooja.isActive ? 'success' : 'error'} size="small" sx={{ fontWeight: 600 }} />
                    {viewPooja.isTrending && <Chip label="🔥 Trending" color="warning" size="small" sx={{ fontWeight: 600 }} />}
                    {viewPooja.isRecommended && <Chip label="⭐ Recommended" color="secondary" size="small" sx={{ fontWeight: 600 }} />}
                    {viewPooja.isNegativeEnergyRemoval && <Chip label="🧿 Neg Energy Rem" color="info" size="small" sx={{ fontWeight: 600 }} />}
                  </Box>
                  <Divider sx={{ width: '100%', mb: 2 }} />
                  <Grid container spacing={2} mb={3}>
                    <Grid item md={6}>
                      <Box sx={{ p: 1.5, backgroundColor: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
                        <Typography variant="caption" color="#7b1fa2" fontWeight={700}>PRICE</Typography>
                        <Typography variant="body1" fontWeight={800} color="#4a148c">₹{viewPooja.basePrice ?? '-'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item md={6}>
                      <Box sx={{ p: 1.5, backgroundColor: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
                        <Typography variant="caption" color="#1976d2" fontWeight={700}>DURATION</Typography>
                        <Typography variant="body1" fontWeight={800} color="#0d47a1">{viewPooja.duration ? `${viewPooja.duration} min` : '-'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1, width: '100%', textAlign: 'left' }}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={800} mb={1.5}>
                      MAPPED ASTROLOGERS ({mappedAstroTotal})
                    </Typography>
                    {mappedAstroLoading ? (
                      <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                    ) : mappedAstrologers.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No mapped astrologers found.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {mappedAstrologers.map((astro) => (
                          <Box key={astro._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eaeaea' }}>
                            <Box>
                              <Typography sx={{ display: 'flex', alignItems: 'center' }} variant="body2" fontWeight={700} color="#333">
                                <Avatar sx={{ width: 24, height: 24, mr: 1 }} src={astro.astrologerId?.profilePic} >{astro.astrologerId?.fullName?.charAt(0) || '-'}</Avatar>
                                {astro.astrologerId?.fullName || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                User Price: ₹{astro.finalUserPrice} | Offer: ₹{astro.finalOfferUserPrice} | Mobile Number: {astro.astrologerId?.mobileNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                Astrologer Share: {astro.astrologerShare}% | Company Share: {astro.companyShare}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {mappedAstroTotal > 10 && (
                          <Box display="flex" justifyContent="center" mt={2} sx={{ width: '100%', overflow: 'hidden' }}>
                            <TablePagination
                              component="div"
                              count={mappedAstroTotal}
                              page={mappedAstroPage - 1}
                              onPageChange={(e, newPage) => {
                                setMappedAstroPage(newPage + 1);
                                fetchMappedAstrologers(viewPooja._id || viewPooja.id, newPage + 1);
                              }}
                              rowsPerPage={10}
                              onRowsPerPageChange={() => { }}
                              rowsPerPageOptions={[]}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <Box width="100%">
                    <Box width="100%" display="flex" justifyContent="space-between">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">CATEGORY</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1.5}>
                          {Array.isArray(viewPooja.categoryId) && viewPooja.categoryId.length > 0
                            ? viewPooja.categoryId.map((c, i) => (
                                <Chip key={i} label={typeof c === 'object' && c !== null ? (c.categoryName || c.name || c._id || '-') : c} size="small" color="primary" variant="outlined" />
                              ))
                            : (viewPooja.categoryId && !Array.isArray(viewPooja.categoryId)
                                ? <Chip label={typeof viewPooja.categoryId === 'object' && viewPooja.categoryId !== null ? (viewPooja.categoryId.categoryName || viewPooja.categoryId.name || viewPooja.categoryId._id || '-') : viewPooja.categoryId} size="small" color="primary" variant="outlined" />
                                : <Typography variant="body2">-</Typography>)}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">TAG</Typography>
                        <Typography variant="body2" fontWeight={600} color="#555" mb={1.5}>
                          {viewPooja.poojaTagId && typeof viewPooja.poojaTagId === 'object'
                            ? viewPooja.poojaTagId.tagName || viewPooja.poojaTagId.name || '-'
                            : viewPooja.poojaTagId || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">CREATED AT</Typography>
                        <Typography variant="body2" fontWeight={600} color="#555" mb={1.5}>
                          {viewPooja.createdAt ? new Date(viewPooja.createdAt).toLocaleString() : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">REGIONS</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                          {(viewPooja.regions || []).length ? viewPooja.regions.map((r, i) => <Chip key={i} label={r} size="small" color="info" sx={{ fontWeight: 600 }} />) : <Typography variant="body2">-</Typography>}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={800}>DESCRIPTION</Typography>
                      <Typography variant="body2" mt={0.5} color="#424242" sx={{ lineHeight: 1.6 }}>{viewPooja.description || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={800}>BENEFITS</Typography>
                      <Typography variant="body2" mt={0.5} color="#424242" sx={{ lineHeight: 1.6 }}>{viewPooja.benefits || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={800}>WHAT HAPPENS AFTER ORDER</Typography>
                      <Typography variant="body2" mt={0.5} color="#424242" sx={{ lineHeight: 1.6 }}>{viewPooja.whatHappensAfterOrder || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={800}>PROCEDURE</Typography>
                      <Typography variant="body2" mt={0.5} color="#424242" sx={{ lineHeight: 1.6 }}>{viewPooja.procedure || '-'}</Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2.5 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} mb={1} display="block">PREFERRED DAYS</Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {(viewPooja.preferredDays || []).length ? viewPooja.preferredDays.map((d, i) => <Chip key={i} label={d} size="small" variant="outlined" sx={{ fontWeight: 600, borderColor: '#7b1fa2', color: '#7b1fa2' }} />) : <Typography variant="body2">-</Typography>}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} mb={1} display="block">INCLUDED SERVICES</Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {viewPooja.includedServices?.sankalp && <Chip label="Sankalp" size="small" color="primary" variant="outlined" />}
                        {viewPooja.includedServices?.templePhotos && <Chip label="Pooja Proof (Pictures)" size="small" color="primary" variant="outlined" />}
                        {viewPooja.includedServices?.videoCall && <Chip label="Pooja On Video Call" size="small" color="primary" variant="outlined" />}
                        {viewPooja.includedServices?.poojaVideo && <Chip label="Pooja Proof (Video)" size="small" color="primary" variant="outlined" />}
                        {viewPooja.includedServices?.prasadDispatch && <Chip label="Prasad Dispatch" size="small" color="primary" variant="outlined" />}
                        {viewPooja.includedServices?.personalizedMantra && <Chip label="Personalized Mantra" size="small" color="primary" variant="outlined" />}
                        {(!viewPooja.includedServices || !Object.values(viewPooja.includedServices).some(Boolean)) && <Typography variant="body2">-</Typography>}
                      </Box>
                    </Grid>
                    {Array.isArray(viewPooja.poojaFaqId) && viewPooja.poojaFaqId.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight={800} mb={1} display="block">FAQs</Typography>
                        <Box display="flex" flexDirection="column" gap={1}>
                          {viewPooja.poojaFaqId.map((faq, index) => (
                            <Accordion key={index} disableGutters sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}>
                              <AccordionSummary expandIcon={<i className="icofont-rounded-down" style={{ fontSize: '18px' }} />} sx={{ backgroundColor: '#f9f9f9', minHeight: 40, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                                <Typography variant="body2" fontWeight={600} color="#333">Q: {faq.question || '-'}</Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ borderTop: '1px solid #e0e0e0', p: 1.5 }}>
                                <Typography variant="body2" color="#555">A: {faq.answer || '-'}</Typography>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditForm(EMPTY_FORM); }}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className={editId ? "icofont-edit" : "icofont-plus"} style={{ color: editId ? '#1976d2' : '#7b1fa2', fontSize: '20px' }}></i>
            {editId ? 'Edit Pooja' : 'Add Pooja'}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 1.5 }}>
          {editLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={12}>
                <TextField fullWidth size="small" label="Name *" value={editForm.name}
                  onChange={(e) => handleEditChange('name', e.target.value)}
                  inputProps={{ maxLength: 120 }}
                  helperText={`${editForm.name.length}/120`}
                  error={editForm.name.length > 120}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Base Price *" type="number"
                  value={editForm.basePrice}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                    handleEditChange('basePrice', val);
                  }}
                  inputProps={{ min: 0, max: 99999 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Duration (min) *" type="number"
                  value={editForm.duration}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || (/^\d{1,3}$/.test(raw) && Number(raw) <= 999)) {
                      handleEditChange('duration', raw);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
                  }}
                  inputProps={{ min: 1, max: 999 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    label="Category"
                    value={editForm.categoryId || []}
                    onChange={(e) => handleEditChange('categoryId', e.target.value)}
                    disabled={categoriesLoading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const cat = categories.find(c => (c._id || c.id) === value);
                          return <Chip key={value} label={cat ? (cat.categoryName || cat.name || cat.title || value) : value} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                        <Checkbox checked={(editForm.categoryId || []).includes(cat._id || cat.id)} />
                        <ListItemText primary={cat.categoryName || cat.name || cat.title || cat._id} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tag</InputLabel>
                  <Select
                    label="Tag"
                    value={editForm.poojaTagId || ''}
                    onChange={(e) => handleEditChange('poojaTagId', e.target.value)}
                    disabled={poojaTagsLoading}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {poojaTags.map((tag) => (
                      <MenuItem key={tag._id || tag.id} value={tag._id || tag.id}>
                        {tag.tagName || tag.name || tag._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Description" multiline minRows={2}
                  value={editForm.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${editForm.description.length}/1000`}
                  error={editForm.description.length > 1000}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Benefits" multiline minRows={2}
                  value={editForm.benefits}
                  onChange={(e) => handleEditChange('benefits', e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${editForm.benefits.length}/1000`}
                  error={editForm.benefits.length > 1000}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="What Happens After Order" multiline minRows={2}
                  value={editForm.whatHappensAfterOrder}
                  onChange={(e) => handleEditChange('whatHappensAfterOrder', e.target.value)}
                  inputProps={{ maxLength: 300 }}
                  helperText={`${editForm.whatHappensAfterOrder.length}/300`}
                  error={editForm.whatHappensAfterOrder.length > 300}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Procedure" multiline minRows={2}
                  value={editForm.procedure}
                  onChange={(e) => handleEditChange('procedure', e.target.value)}
                  inputProps={{ maxLength: 300 }}
                  helperText={`${editForm.procedure.length}/300`}
                  error={editForm.procedure.length > 300}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="datetime-local" label="Start Date Time"
                  value={editForm.startDateTime}
                  onChange={(e) => handleEditChange('startDateTime', e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="datetime-local" label="End Date Time"
                  value={editForm.endDateTime}
                  onChange={(e) => handleEditChange('endDateTime', e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="datetime-local" label="Booking Open Till"
                  value={editForm.bookingOpenTill}
                  onChange={(e) => handleEditChange('bookingOpenTill', e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} mb={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} mb={0.8}>Upload Image</Typography>
                <TextField fullWidth size="small" type="file"
                  onChange={handleImageUpload}
                  inputProps={{ accept: "image/*" }}
                />
                {editForm.image && (
                  <Box mt={1}>
                    <img src={editForm.image} alt="Preview" style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={12} mb={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1}>FAQs</Typography>
                <Box border="1px solid #c4c4c4" borderRadius="4px" p={1.5} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    Selected FAQs: <strong>{editForm.poojaFaqId?.length || 0}</strong>
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => {
                      setTempSelectedFaqs(editForm.poojaFaqId || []);
                      setFaqSelectPage(1);
                      fetchPoojaFaqs(1);
                      setFaqSelectOpen(true);
                    }}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    Manage FAQs
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Preferred Days</InputLabel>
                  <Select
                    multiple
                    label="Preferred Days"
                    value={editForm.preferredDays}
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      const allIndividualDays = DAYS_OPTIONS.filter(d => d !== "All");

                      if (value.includes('All')) {
                        if (editForm.preferredDays.length === allIndividualDays.length) {
                          handleEditChange('preferredDays', []);
                        } else {
                          handleEditChange('preferredDays', allIndividualDays);
                        }
                      } else {
                        handleEditChange('preferredDays', value.filter(v => v !== 'All'));
                      }
                    }}
                    renderValue={(selected) => {
                      const allIndividualDays = DAYS_OPTIONS.filter(d => d !== "All");
                      if (selected.length === allIndividualDays.length) return "All";
                      return selected.join(', ');
                    }}
                  >
                    {DAYS_OPTIONS.map((o) => {
                      const allIndividualDays = DAYS_OPTIONS.filter(d => d !== "All");
                      const isChecked = o === "All"
                        ? editForm.preferredDays.length === allIndividualDays.length
                        : editForm.preferredDays.indexOf(o) > -1;
                      return (
                        <MenuItem key={o} value={o}>
                          <Checkbox checked={isChecked} />
                          {o}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={editForm.isTrending}
                    onChange={(e) => handleEditChange('isTrending', e.target.checked)}
                    color="warning" />}
                  label="🔥 Mark as Trending" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1}>Regions</Typography>
                <Grid container spacing={1}>
                  {REGION_OPTIONS.map((r) => (
                    <Grid item xs={6} sm={4} key={r}>
                      <FormControlLabel
                        control={<Checkbox checked={editForm.regions.includes(r)} onChange={() => handleEditRegionToggle(r)} color="primary" />}
                        label={r}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1}>Included Services</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.sankalp || false} onChange={(e) => handleIncludedServiceChange('sankalp', e.target.checked)} color="primary" />} label="Sankalp" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.templePhotos || false} onChange={(e) => handleIncludedServiceChange('templePhotos', e.target.checked)} color="primary" />} label="Pooja Proof (Pictures)" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.videoCall || false} onChange={(e) => handleIncludedServiceChange('videoCall', e.target.checked)} color="primary" />} label="Pooja On Video Call" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.poojaVideo || false} onChange={(e) => handleIncludedServiceChange('poojaVideo', e.target.checked)} color="primary" />} label="Pooja Proof (Video)" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.personalizedMantra || false} onChange={(e) => handleIncludedServiceChange('personalizedMantra', e.target.checked)} color="primary" />} label="Personalized Mantra" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={editForm.includedServices?.prasadDispatch || false} disabled onChange={(e) => handleIncludedServiceChange('prasadDispatch', e.target.checked)} color="primary" />} label="Prasad Dispatch" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => { setEditOpen(false); setEditForm(EMPTY_FORM); }} variant="outlined"
            disabled={actionLoading.edit} sx={{ textTransform: 'none', borderRadius: '8px' }}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary"
            disabled={editLoading || actionLoading.edit} sx={{ textTransform: 'none', borderRadius: '8px', minWidth: 120 }}>
            {actionLoading.edit ? <CircularProgress size={20} color="inherit" /> : (editId ? 'Save Changes' : 'Create Pooja')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className="icofont-link" style={{ color: '#388e3c', fontSize: '20px' }}></i>
            Add Astrologer Mapping
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ bgcolor: '#f3f4f6', borderRadius: '10px', p: 2, mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" mb={1}>POOJA DETAILS</Typography>
            <Typography variant="body1" fontWeight={500} mb={1}>{mapPoojaName}</Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box sx={{ bgcolor: '#fff', borderRadius: '8px', px: 1.5, py: 0.8, minWidth: 80, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">CATEGORY</Typography>
                <Typography variant="body2" fontWeight={500}>{mapPoojaDetails?.category || '-'}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#e3f2fd', borderRadius: '8px', px: 1.5, py: 0.8, minWidth: 80, textAlign: 'center', border: '1px solid #bbdefb' }}>
                <Typography variant="caption" color="#1976d2" fontWeight={500} display="block">DURATION</Typography>
                <Typography variant="body2" fontWeight={500} color="#0d47a1">{mapPoojaDetails?.duration ? `${mapPoojaDetails.duration} min` : '-'}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#f3e5f5', borderRadius: '8px', px: 1.5, py: 0.8, minWidth: 80, textAlign: 'center', border: '1px solid #e1bee7' }}>
                <Typography variant="caption" color="#7b1fa2" fontWeight={500} display="block">BASE PRICE</Typography>
                <Typography variant="body2" fontWeight={500} color="#4a148c">{mapPoojaDetails?.basePrice != null ? `₹${mapPoojaDetails.basePrice}` : '-'}</Typography>
              </Box>
            </Box>
          </Box>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              {mappingRows.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #ddd",
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography fontWeight={500}>
                        Mapping #{index + 1}
                      </Typography>

                      {(() => {
                        const basePrice = Number(mapPoojaDetails?.basePrice) || 0;
                        const duration = Number(mapPoojaDetails?.duration) || 0;
                        const astroCost = Number(row.selectedAstrologer?.videoCall?.offerPricePerMinute) || 0;
                        const suggestivePrice = basePrice + (duration * astroCost);

                        return row.selectedAstrologer ? (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" }, fontWeight: 500 }}
                            onClick={() => updateMappingRow(index, "finalOfferUserPrice", suggestivePrice)}
                          >
                            {`{Suggestive Offer Price: = ${basePrice} + (${duration} * ${astroCost}) = ${suggestivePrice}}`}
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                    {mappingRows.length > 1 && (
                      <Button
                        color="error"
                        size="small"
                        onClick={() => removeMappingRow(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Button
                          size="small"
                          disabled={astroPage <= 1}
                          onClick={() => {
                            const page = astroPage - 1;
                            setAstroPage(page);
                            fetchAstrologers(page, astroSearch);
                          }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2">
                          Page {astroPage} / {astroPagination.totalPages}
                        </Typography>
                        <Button
                          size="small"
                          disabled={astroPage >= astroPagination.totalPages}
                          onClick={() => {
                            const page = astroPage + 1;
                            setAstroPage(page);
                            fetchAstrologers(page, astroSearch);
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        loading={astroLoading}
                        options={astrologers.filter((a) => {
                          const otherSelectedIds = mappingRows
                            .filter((r, i) => i !== index)
                            .map((r) => r.astrologerId || r.selectedAstrologer?._id)
                            .filter(Boolean);
                          return !otherSelectedIds.includes(a._id) || a._id === (row.astrologerId || row.selectedAstrologer?._id);
                        })}
                        filterOptions={(x) => x}
                        getOptionLabel={(option) =>
                          `${option?.fullName || ""} (Phone: ${option?.mobileNumber || "-"} | Price: ₹${option?.videoCall?.offerPricePerMinute ?? "-"})`
                        }
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        value={row.selectedAstrologer || null}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "input") {
                            debouncedAstroSearch(newInputValue);
                          }
                        }}
                        onChange={(e, value) => {
                          setMappingRows((prev) =>
                            prev.map((r, i) =>
                              i === index
                                ? { ...r, astrologerId: value?._id || "", selectedAstrologer: value || null }
                                : r
                            )
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Astrologer"
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Price"
                        type="number"
                        value={row.finalUserPrice}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || Number(val) >= 0) updateMappingRow(index, "finalUserPrice", val);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Offer Price"
                        type="number"
                        value={row.finalOfferUserPrice}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || Number(val) >= 0) updateMappingRow(index, "finalOfferUserPrice", val);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Company Share %"
                        type="number"
                        value={row.companyShare}
                        inputProps={{ min: 0, max: 100 }}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                            setMappingRows((prev) => prev.map((r, i) => i === index ? { ...r, companyShare: val, astrologerShare: val === '' ? '' : String(100 - Number(val)) } : r));
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Astrologer Share %"
                        type="number"
                        disabled
                        value={row.astrologerShare}
                        inputProps={{ min: 0, max: 100 }}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                            setMappingRows((prev) => prev.map((r, i) => i === index ? { ...r, astrologerShare: val, companyShare: val === '' ? '' : String(100 - Number(val)) } : r));
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        label="Pooja Description (Optional)"
                        value={row.poojaDescription}
                        onChange={(e) =>
                          updateMappingRow(
                            index,
                            "poojaDescription",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    {(row.finalUserPrice || row.finalOfferUserPrice || row.companyShare || row.astrologerShare) ? (
                      <Grid item xs={12}>
                        <Box sx={{ bgcolor: '#f0f9f0', border: '1px solid #c8e6c9', borderRadius: '8px', p: 1.5 }}>
                          <Typography variant="caption" color="#2e7d32" fontWeight={700} display="block" mb={1}>PRICE CALCULATION</Typography>
                          <Box display="flex" gap={2} flexWrap="wrap">
                            {row.finalUserPrice ? (
                              <Box textAlign="center">
                                <Typography variant="caption" color="text.secondary" display="block">Price</Typography>
                                <Typography variant="body2" fontWeight={700}>₹{row.finalUserPrice}</Typography>
                              </Box>
                            ) : null}
                            {row.finalOfferUserPrice ? (
                              <Box textAlign="center">
                                <Typography variant="caption" color="text.secondary" display="block">Offer Price</Typography>
                                <Typography variant="body2" fontWeight={700}>₹{row.finalOfferUserPrice}</Typography>
                              </Box>
                            ) : null}
                            {row.finalOfferUserPrice && row.companyShare ? (
                              <Box textAlign="center">
                                <Typography variant="caption" color="text.secondary" display="block">Company ({row.companyShare}%)</Typography>
                                <Typography variant="body2" fontWeight={700}>₹{Math.round((Number(row.finalOfferUserPrice) * Number(row.companyShare)) / 100)}</Typography>
                              </Box>
                            ) : null}
                            {row.finalOfferUserPrice && row.astrologerShare ? (
                              <Box textAlign="center">
                                <Typography variant="caption" color="text.secondary" display="block">Astrologer ({row.astrologerShare}%)</Typography>
                                <Typography variant="body2" fontWeight={700}>₹{Math.round((Number(row.finalOfferUserPrice) * Number(row.astrologerShare)) / 100)}</Typography>
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      </Grid>
                    ) : null}
                  </Grid>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={addMappingRow}
                fullWidth
              >
                + Add More Mapping
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setMapOpen(false)} variant="outlined" disabled={mapLoading}
            sx={{ textTransform: 'none', borderRadius: '8px' }}>Cancel</Button>
          <Button onClick={handleMappingSubmit} variant="contained" color="success"
            disabled={mapLoading} sx={{ textTransform: 'none', borderRadius: '8px', minWidth: 140 }}>
            {mapLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Mapping'}
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={poojaLoading || exportLoading || Object.values(actionLoading).some(Boolean)}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog open={unmapConfirmOpen} onClose={() => !unmapLoading && setUnmapConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Unmap</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to unmap this astrologer?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmapConfirmOpen(false)} color="inherit" disabled={unmapLoading}>
            Cancel
          </Button>
          <Button onClick={handleUnmapAstrologer} variant="contained" color="error" disabled={unmapLoading}>
            {unmapLoading ? <CircularProgress size={24} color="inherit" /> : 'Yes, Unmap'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={faqSelectOpen} onClose={() => setFaqSelectOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
          Select FAQs
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, minHeight: '300px' }}>
          {poojaFaqsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : (
            <List sx={{ p: 0 }}>
              {poojaFaqs.map((faq) => (
                <ListItem key={faq._id || faq.id} disablePadding divider>
                  <ListItemButton onClick={() => handleToggleTempFaq(faq._id || faq.id)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={tempSelectedFaqs.includes(faq._id || faq.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={faq.question} 
                      primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                      secondary={faq.answer}
                      secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', sx: { whiteSpace: 'normal', mt: 0.5 } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {poojaFaqs.length === 0 && (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">No FAQs found.</Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Pagination 
            count={faqPaginationDetail.totalPages || 1} 
            page={faqSelectPage} 
            onChange={(e, p) => { 
              setFaqSelectPage(p); 
              fetchPoojaFaqs(p); 
            }} 
            color="primary" 
            size="small"
          />
          <Box display="flex" gap={1}>
            <Button onClick={() => setFaqSelectOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>Cancel</Button>
            <Button onClick={() => { handleEditChange('poojaFaqId', tempSelectedFaqs); setFaqSelectOpen(false); }} variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }}>Confirm</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PoojaList;